import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../api/axiosInstance';
import { showNotification } from './notificationSlice';

const BASE = 'techsavanna_pos.api.staff_performance_api';

const ENDPOINTS = {
  getPerformance: `${BASE}.get_staff_performance`,
  getCommissionRules: `${BASE}.get_commission_rules`,
  createCommissionRule: `${BASE}.create_commission_rule`,
  getCommissionSummary: `${BASE}.get_commission_summary`,
};

const extract = (res) =>
  res.data?.message && typeof res.data.message === 'object'
    ? res.data.message
    : res.data?.message || res.data;

const extractErr = (err) => {
  const d = err.response?.data;
  if (d?._server_messages) {
    try {
      const msgs = JSON.parse(d._server_messages);
      const first = Array.isArray(msgs) ? msgs[0] : null;
      if (first) {
        const p = typeof first === 'string' ? JSON.parse(first) : first;
        if (p?.message) return String(p.message).replace(/<[^>]*>/g, '').trim();
      }
    } catch { /* fall through */ }
  }
  return d?.message || err.message || 'An error occurred';
};

export const getStaffPerformance = createAsyncThunk('staffPerf/get', async (params = {}, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get(ENDPOINTS.getPerformance, { params });
    return extract(res);
  } catch (err) {
    return rejectWithValue(extractErr(err));
  }
});

export const getCommissionRules = createAsyncThunk('staffPerf/rules', async (params = {}, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get(ENDPOINTS.getCommissionRules, { params });
    return extract(res);
  } catch (err) {
    return rejectWithValue(extractErr(err));
  }
});

export const createCommissionRule = createAsyncThunk('staffPerf/createRule', async (data, { dispatch, rejectWithValue }) => {
  try {
    const res = await axiosInstance.post(ENDPOINTS.createCommissionRule, data);
    const result = extract(res);
    dispatch(showNotification({ message: result.message || 'Commission rule saved', type: 'success' }));
    return result;
  } catch (err) {
    const msg = extractErr(err);
    dispatch(showNotification({ message: msg, type: 'error' }));
    return rejectWithValue(msg);
  }
});

export const getCommissionSummary = createAsyncThunk('staffPerf/commissionSummary', async (params = {}, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get(ENDPOINTS.getCommissionSummary, { params });
    return extract(res);
  } catch (err) {
    return rejectWithValue(extractErr(err));
  }
});

const staffPerformanceSlice = createSlice({
  name: 'staffPerformance',
  initialState: {
    performance: [],
    commissionRules: [],
    commissionSummary: [],
    loading: false,
    error: null,
    dateRange: { from_date: null, to_date: null },
  },
  reducers: { clearError: (state) => { state.error = null; } },
  extraReducers: (builder) => {
    const loading = (state) => { state.loading = true; state.error = null; };
    const failed = (state, action) => { state.loading = false; state.error = action.payload; };

    builder
      .addCase(getStaffPerformance.pending, loading)
      .addCase(getStaffPerformance.fulfilled, (state, action) => {
        state.loading = false;
        state.performance = action.payload?.performance || [];
        state.dateRange = {
          from_date: action.payload?.from_date,
          to_date: action.payload?.to_date
        };
      })
      .addCase(getStaffPerformance.rejected, failed)
      .addCase(getCommissionRules.pending, loading)
      .addCase(getCommissionRules.fulfilled, (state, action) => {
        state.loading = false;
        state.commissionRules = action.payload?.rules || [];
      })
      .addCase(getCommissionRules.rejected, failed)
      .addCase(createCommissionRule.fulfilled, (state, action) => {
        if (action.payload?.rule) state.commissionRules.unshift(action.payload.rule);
      })
      .addCase(getCommissionSummary.pending, loading)
      .addCase(getCommissionSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.commissionSummary = action.payload?.summary || [];
      })
      .addCase(getCommissionSummary.rejected, failed);
  },
});

export const { clearError } = staffPerformanceSlice.actions;
export default staffPerformanceSlice.reducer;
